import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, makeStyles, Typography } from '@popmenu/common-ui';
import type { UserTasteProfileDishFragment } from '~/libs/gql/queries/eat/userTasteProfileQuery.generated';
import CustomImg from '~/shared/CustomImg';
import { RestaurantLinkButton } from '../RestaurantLinkButton';
import { TasteMatchChips } from '../TasteMatchChips';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
  },
  name: {
    fontSize: '1rem',
    fontWeight: 600,
  },
  photo: {
    height: '100%',
    left: 0,
    objectFit: 'cover',
    position: 'absolute',
    top: 0,
    width: '100%',
  },
  photoContainer: {
    backgroundColor: theme.palette.gray.main,
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    paddingTop: '75%',
    position: 'relative',
  },
  restaurantName: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
}));

type ProfileDishCardProps = {
  dish: UserTasteProfileDishFragment
  // The diner's likes that the dish shows, for its chips
  traits: { feature: string, isConfident: boolean }[]
}

export const ProfileDishCard = ({ dish, traits }: ProfileDishCardProps) => {
  const classes = useStyles();

  return (
    <Box className={classes.container} component="article">
      <Box className={classes.photoContainer}>
        {dish.featuredPhoto?.photoUrl && (
          <CustomImg alt="" className={classes.photo} size="sm" src={dish.featuredPhoto.photoUrl} />
        )}
      </Box>
      <Box>
        <Typography className={classes.name} component="h3">
          {dish.displayName}
        </Typography>
        {dish.restaurant && dish.location && (
          <Typography className={classes.restaurantName}>
            <FormattedMessage
              defaultMessage="{restaurantName} in {city}, {stateOrCountry}"
              values={{
                city: dish.location.city,
                restaurantName: dish.restaurant.name,
                stateOrCountry: dish.location.state || dish.location.countryName,
              }}
            />
          </Typography>
        )}
      </Box>
      <TasteMatchChips traits={traits} />
      {dish.restaurantLink && (
        <RestaurantLinkButton dishName={dish.displayName} link={dish.restaurantLink} restaurantName={dish.restaurant?.name ?? ''} />
      )}
    </Box>
  );
};
